import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreateCaptureSessionRequest } from '../../interfaces/api/create-capture-session-request.interface';
import { ICaptureSessionsService } from '../../interfaces/services/capture-sessions-service.interface';
import { CaptureSession } from '../../interfaces/state/entities/capture-session.interface';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CaptureSessionsService implements ICaptureSessionsService {
  private http = inject(HttpClient);
  private apiUrl = environment.MayaTestToolApiBaseUrl;

  getCaptureSessions(floorId: string): Observable<CaptureSession[]> {
    const params = new HttpParams().set('floorId', floorId);
    return this.http.get<unknown>(`${this.apiUrl}/captureSessions`, { params }).pipe(
      map((body) => {
        const sessions = this.extractSessionsArray(body);
        return sessions.map((s) => this.mapCaptureSessionFromApi(s, floorId));
      })
    );
  }

  /** API may return a bare array or a wrapper object (camelCase or PascalCase). */
  private extractSessionsArray(body: unknown): any[] {
    if (Array.isArray(body)) {
      return body;
    }
    if (body != null && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      const keys = ['sessions', 'captureSessions', 'Sessions', 'CaptureSessions'];
      for (const key of keys) {
        const v = o[key];
        if (Array.isArray(v)) {
          return v;
        }
      }
    }
    return [];
  }

  addCaptureSession(body: CreateCaptureSessionRequest): Observable<CaptureSession> {
    return this.http.post<any>(`${this.apiUrl}/captureSessions`, body).pipe(
      map((raw) => this.mapCaptureSessionFromApi(raw, body.floorId))
    );
  }

  private normalizeSessionRaw(raw: any): Record<string, unknown> {
    if (raw != null && typeof raw === 'object') {
      const nested = (raw as Record<string, unknown>)['captureSession'];
      if (nested != null && typeof nested === 'object') {
        return { ...(nested as Record<string, unknown>), ...(raw as Record<string, unknown>) };
      }
      return raw as Record<string, unknown>;
    }
    return {};
  }

  private mapCaptureSessionFromApi(rawInput: unknown, floorIdFallback: string): CaptureSession {
    const raw = this.normalizeSessionRaw(rawInput);
    const startedRaw = raw['StartedAt'] ?? raw['startedAt'] ?? raw['sessionStartedAt'];
    const dateCreatedRaw = raw['DateCreated'] ?? raw['dateCreated'] ?? raw['createdAt'];
    return {
      id:
        String(
          raw['Id'] ??
            raw['SessionId'] ??
            raw['CaptureSessionId'] ??
            raw['captureSessionId'] ??
            raw['id'] ??
            ''
        ) || '',
      floorId: String(raw['FloorId'] ?? raw['floorId'] ?? floorIdFallback),
      name: String(
        raw['CaptureSessionName'] ??
          raw['captureSessionName'] ??
          raw['Name'] ??
          raw['SessionName'] ??
          raw['name'] ??
          ''
      ),
      camera: String(raw['Camera'] ?? raw['camera'] ?? raw['cameraName'] ?? ''),
      device: String(raw['Device'] ?? raw['device'] ?? raw['deviceName'] ?? ''),
      captureCount: Number(raw['CaptureCount'] ?? raw['captureCount'] ?? 0),
      dateCreated: dateCreatedRaw ? new Date(String(dateCreatedRaw)) : new Date(),
      startedAt: startedRaw ? new Date(String(startedRaw)) : undefined,
      drawingRevision:
        (raw['DrawingRevision'] ??
          raw['RevisionNumber'] ??
          raw['drawingRevision'] ??
          raw['currentDrawingRevision']) as number | undefined,
    };
  }

  incrementCaptureCount(sessionId: string): void {
    // In real implementation, this would typically be handled server-side
    // when a capture is added, so this method may not be needed
    // or could make a PATCH request to update the count
    // For now, it's a no-op as the count should come from the API
  }
}
